import * as SentryAngular from '@sentry/angular';
import * as Sentry from '@sentry/capacitor';
import type {Breadcrumb, ErrorEvent, Event, EventHint} from '@sentry/angular';
import {HttpErrorResponse} from "@angular/common/http";
import {environment} from "../../environments/environment";
import {APP_VERSION} from "./app-version";
import {isUserCancelled} from "./error-filters";

/**
 * Starts Sentry in the browser and the native shells. Called from main.ts only, so it never runs
 * during SSR. An empty DSN leaves Sentry off, and every Sentry call elsewhere becomes a no-op.
 *
 * Uncaught errors (rejected promises, rxjs subscribe without an error callback, timer throws)
 * reach Sentry through its default window `error`/`unhandledrejection` handlers, silently. They
 * are deliberately not routed to Angular's ErrorHandler, because GlobalErrorHandle toasts.
 */
export function initSentry(): void {
  const {dsn, environment: sentryEnvironment} = environment.sentry;
  if (dsn === '') {
    return;
  }

  Sentry.init({
    dsn,
    environment: sentryEnvironment,
    release: `homey-app@${APP_VERSION}`,
    // Errors are sampled at 100% by default; tracing and replay stay off by not enabling them.
    sendDefaultPii: false,
    // GlobalErrorHandle already reports failed HTTP calls the app didn't handle.
    enableCaptureFailedRequests: false,
    // Piet: drop nothing that is a real error, including what GlobalErrorHandle hides from users.
    // Mute noise with Sentry's inbound filters instead. Cancellations aren't errors at all.
    beforeSend: (event, hint) => isUserCancelled(hint.originalException) ? null : scrubEvent(event),
    // First, so Dedupe compares described HTTP errors, not identical "Object captured" events.
    integrations: defaults => [{name: 'HttpErrorResponse', processEvent: describeHttpErrorResponse}, ...defaults],
    beforeBreadcrumb: scrubBreadcrumb,
  }, SentryAngular.init);
}

/**
 * Reports an error GlobalErrorHandle handled, whether or not it was shown. Sentry marks it
 * `handled: true`, which tells it apart from the silent uncaught ones.
 */
export function captureHandledError(error: unknown): void {
  Sentry.captureException(error);
}

/** A failed bootstrap leaves a blank screen and no later event to carry a breadcrumb. */
export function captureBootstrapError(error: unknown): void {
  Sentry.captureException(error, {mechanism: {type: 'bootstrap', handled: false}});
}

export function setSentryUser(id: string | undefined): void {
  Sentry.setUser(id === undefined ? null : {id});
}

const emailPattern = /[\w.+-]+@[\w-]+(\.[\w-]+)+/g;
// US numbers as people write them. Longer digit runs (ms timestamps, most ids) survive; a bare
// 10-digit run such as a seconds timestamp is masked too, which errs on the safe side.
const phonePattern = /(?<!\d)(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}(?!\d)/g;
const jwtPattern = /eyJ[\w-]+\.[\w-]+\.[\w-]+/g;
const bearerPattern = /Bearer\s+\S+/gi;

// Web URLs and the app's own deep-link schemes (OAuth callbacks carry ?code=). The match never
// ends on prose punctuation, so "…?id=1: 500" keeps its colon.
const urlPattern = /\b(?:https?|capacitor|io\.myhomey\.app):\/\/[^\s"'<>]*[^\s"'<>:,;.)]/g;

function scrubText(text: string): string {
  return text
    // e.g. "Http failure response for https://…?id=…" logged to the console before a capture.
    .replace(urlPattern, stripQuery)
    .replace(jwtPattern, '[token]')
    .replace(bearerPattern, 'Bearer [token]')
    .replace(emailPattern, '[email]')
    .replace(phonePattern, '[phone]');
}

/** Query strings and fragments carry OAuth codes, tokens and emails; the path is enough. */
function stripQuery(url: string): string {
  return url.split(/[?#]/)[0];
}

/** The URL's path, with segments holding a digit as `:id`: one issue per endpoint, not per apartment. */
function routeOf(url: string): string {
  let path: string;
  try {
    path = new URL(url).pathname;
  } catch {
    path = stripQuery(url);
  }
  return path.split('/').map(segment => /\d/.test(segment) ? ':id' : segment).join('/');
}

/**
 * HttpErrorResponse isn't an Error, so Sentry would title it "Object captured as exception" and
 * serialize it whole (URL with query string, response body). Both the handled and the uncaught
 * path come through here, so describe it once, by status and route.
 *
 * It runs as an integration rather than in beforeSend because Dedupe runs before beforeSend:
 * two different failed requests in a row would look identical to it and the second be dropped.
 */
function describeHttpErrorResponse(event: Event, hint: EventHint): Event {
  const error = hint.originalException;
  if (!(error instanceof HttpErrorResponse)) {
    return event;
  }
  const status = error.status.toString();
  const route = scrubText(routeOf(error.url ?? 'unknown'));
  // Keep the mechanism: it carries handled vs uncaught.
  const [original] = event.exception?.values ?? [];
  event.exception = {values: [{...original, type: 'HttpErrorResponse', value: `HTTP ${status} ${route}`}]};
  event.fingerprint = ['http-error', status, route];
  event.tags = {...event.tags, http_status: status};
  return event;
}

function scrubEvent(event: ErrorEvent): ErrorEvent {
  // Non-Error values are serialized whole into extra (URLs, bodies, tokens); the app sets no extras.
  delete event.extra;
  if (event.request) {
    const userAgent = event.request.headers?.['User-Agent'];
    event.request = {
      url: event.request.url === undefined ? undefined : stripQuery(event.request.url),
      // Sentry derives browser and OS from it; Referer and the rest can carry query strings.
      headers: userAgent === undefined ? undefined : {'User-Agent': userAgent},
    };
  }
  if (event.user) {
    // Belt and braces: setSentryUser only sets the id, but nothing else may add more.
    event.user = {id: event.user.id};
  }
  if (event.message) {
    event.message = scrubText(event.message);
  }
  for (const exception of event.exception?.values ?? []) {
    if (exception.value) {
      exception.value = scrubText(exception.value);
    }
  }
  // JS breadcrumbs were scrubbed as they were recorded, but on iOS/Android the native SDK merges
  // in its own, which never pass through beforeBreadcrumb.
  event.breadcrumbs = event.breadcrumbs?.map(scrubBreadcrumb);
  return event;
}

function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const scrubbed = {
    ...breadcrumb,
    message: breadcrumb.message === undefined ? undefined : scrubText(breadcrumb.message),
  };
  if (breadcrumb.data === undefined) {
    return scrubbed;
  }
  // Console breadcrumbs keep the raw arguments; the scrubbed message is enough.
  const {arguments: _arguments, ...data} = breadcrumb.data;
  for (const key of ['url', 'from', 'to']) {
    const value = data[key];
    if (typeof value === 'string') {
      data[key] = stripQuery(value);
    }
  }
  return {...scrubbed, data};
}
